import { useState } from 'react';
import { Check, Copy, RefreshCw, AlertTriangle, RotateCcw } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import {
  rcGetOfferings,
  rcGetProducts,
  isRevenueCatAvailable,
  rcAppUserID,
  rcCustomerInfo,
  rcResetSession,
} from '../lib/revenuecat';

// Product identifiers as configured in App Store Connect & attached to
// the RevenueCat offering. Used to compare StoreKit (`getProducts`)
// answers against the Offerings (`getOfferings`) answers.
const STORE_PRODUCT_IDS = [
  'slimmy_premium_monthly',
  'slimmy_premium_yearly',
];

// 10 primary App Store country codes we want to verify the subscription
// is priced in. These are the markets where Slimmy is expected to sell.
// The "expected_currency" column is what Apple App Store Connect *should*
// have configured — if the live RC SDK returns a different currency for
// the current device, it's a sandbox/storefront mis-configuration.
const TARGET_COUNTRIES = [
  { code: 'IT', name: 'Italia',         expected_currency: 'EUR', flag: '🇮🇹' },
  { code: 'DE', name: 'Germania',       expected_currency: 'EUR', flag: '🇩🇪' },
  { code: 'FR', name: 'Francia',        expected_currency: 'EUR', flag: '🇫🇷' },
  { code: 'ES', name: 'Spagna',         expected_currency: 'EUR', flag: '🇪🇸' },
  { code: 'PT', name: 'Portogallo',     expected_currency: 'EUR', flag: '🇵🇹' },
  { code: 'GB', name: 'Regno Unito',    expected_currency: 'GBP', flag: '🇬🇧' },
  { code: 'US', name: 'Stati Uniti',    expected_currency: 'USD', flag: '🇺🇸' },
  { code: 'BR', name: 'Brasile',        expected_currency: 'BRL', flag: '🇧🇷' },
  { code: 'MX', name: 'Messico',        expected_currency: 'MXN', flag: '🇲🇽' },
  { code: 'JP', name: 'Giappone',       expected_currency: 'JPY', flag: '🇯🇵' },
];

// Drop circular refs / native Proxy fields when serializing the offerings
// blob so JSON.stringify doesn't blow up on iOS WKWebView.
const safeJson = (obj) => {
  try {
    return JSON.stringify(obj, (_k, v) => {
      if (v && typeof v === 'object') {
        try { JSON.stringify(v); return v; }
        catch { return '[Unserializable]'; }
      }
      return v;
    }, 2);
  } catch (e) {
    return `[JSON error: ${e?.message || e}]`;
  }
};

export default function RevenueCatPriceDiagnostic({ isDark }) {
  const [running, setRunning] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [resetLog, setResetLog] = useState(null);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);

  const runDiagnostic = async () => {
    setRunning(true);
    setError(null);
    setResult(null);
    const report = {
      timestamp: new Date().toISOString(),
      platform: Capacitor.getPlatform(),
      isNativePlatform: Capacitor.isNativePlatform(),
      isPluginAvailable_Purchases: Capacitor.isPluginAvailable('Purchases'),
      isRevenueCatAvailable: false,
      app_language: navigator.language,
      app_locales: navigator.languages,
      appUserID: null,
      customerInfo: null,
      currentOfferingIdentifier: null,
      offerings: null,
      packages_extracted: null,
      // StoreKit-direct query (the new source of truth for prices)
      storekit_products: null,
      // Side-by-side comparison: StoreKit vs Offering for each product
      price_comparison: null,
      detected_storefront: null,
      fetch_error: null,
    };
    try {
      report.isRevenueCatAvailable = isRevenueCatAvailable();
      if (!report.isRevenueCatAvailable) {
        report.fetch_error = 'RevenueCat not available on this build (only works on iOS/Android native).';
      } else {
        report.appUserID = await rcAppUserID();
        report.customerInfo = await rcCustomerInfo();
        const offerings = await rcGetOfferings();
        report.offerings = offerings;
        report.currentOfferingIdentifier = offerings?.identifier || null;
        const pkgs = offerings?.availablePackages || [];
        report.packages_extracted = pkgs.map((p) => {
          const prod = p?.product || {};
          return {
            identifier: p.identifier,
            packageType: p.packageType,
            offeringIdentifier: p.offeringIdentifier || p.presentedOfferingIdentifier,
            product_identifier: prod.identifier,
            product_price: prod.price,
            product_priceString: prod.priceString,
            product_localizedPriceString: prod.localizedPriceString,
            product_currencyCode: prod.currencyCode,
            product_subscriptionPeriod: prod.subscriptionPeriod,
            product_introPrice: prod.introPrice,
            product_defaultOption: prod.defaultOption,
            product_keys: Object.keys(prod),
          };
        });

        // Direct StoreKit query — should reflect the device's real
        // Apple ID storefront, even when the RC offering returns the
        // dashboard's anonymous fallback.
        const skProducts = await rcGetProducts(STORE_PRODUCT_IDS);
        report.storekit_products = (skProducts || []).map((prod) => ({
          identifier: prod?.identifier || prod?.productIdentifier,
          price: prod?.price ?? prod?.priceAmount,
          priceString: prod?.priceString || prod?.localizedPriceString,
          currencyCode: prod?.currencyCode || prod?.currency,
          subscriptionPeriod: prod?.subscriptionPeriod,
        }));

        // Build a side-by-side comparison keyed by product identifier.
        // For each product we compare StoreKit (`getProducts`) vs the
        // RC Offering (`getOfferings`) and flag mismatches in
        // priceString / currencyCode — those are the values that must
        // match the Apple payment sheet exactly.
        const skById = {};
        for (const p of report.storekit_products) {
          if (p.identifier) skById[p.identifier] = p;
        }
        const offByProductId = {};
        for (const pkg of report.packages_extracted) {
          if (pkg.product_identifier) offByProductId[pkg.product_identifier] = pkg;
        }
        report.price_comparison = STORE_PRODUCT_IDS.map((id) => {
          const sk = skById[id];
          const off = offByProductId[id];
          const sk_priceString = sk?.priceString ?? null;
          const off_priceString = off?.product_priceString ?? off?.product_localizedPriceString ?? null;
          const sk_currency = sk?.currencyCode ?? null;
          const off_currency = off?.product_currencyCode ?? null;
          return {
            product_id: id,
            storekit_priceString:  sk_priceString,
            offering_priceString:  off_priceString,
            priceString_match: sk_priceString && off_priceString
              ? sk_priceString === off_priceString
              : null,
            storekit_currency: sk_currency,
            offering_currency: off_currency,
            currency_match: sk_currency && off_currency
              ? sk_currency === off_currency
              : null,
          };
        });

        // Storefront detection: ALWAYS prefer StoreKit, fall back to
        // the offering only when StoreKit returned nothing (e.g. the
        // user has no Apple ID signed in).
        const skCurr = report.storekit_products?.[0]?.currencyCode;
        const offCurr = report.packages_extracted[0]?.product_currencyCode;
        const firstCurr = skCurr || offCurr;
        if (firstCurr) {
          const match = TARGET_COUNTRIES.find((c) => c.expected_currency === firstCurr);
          report.detected_storefront = match
            ? `${match.flag} ${match.name} (${firstCurr}) — fonte: ${skCurr ? 'StoreKit' : 'Offering'}`
            : `Sconosciuto (${firstCurr}) — fonte: ${skCurr ? 'StoreKit' : 'Offering'}`;
        } else {
          report.detected_storefront = 'Non rilevato — né StoreKit né Offering hanno restituito currencyCode';
        }

        // Italy fallback detection (matches Premium.js logic). When
        // the SDK returns non-EUR on an Italian device, the Premium
        // UI patches the price with App Store Connect Italian values
        // so the displayed amount matches the Apple payment sheet.
        const isIos = Capacitor.getPlatform() === 'ios';
        const lang = (navigator.language || '').toLowerCase();
        const langs = (navigator.languages || []).map((l) => (l || '').toLowerCase());
        const isItLocale = lang.startsWith('it') || langs.some((l) => l.startsWith('it'));
        const wrongCurrency = !firstCurr || firstCurr !== 'EUR';
        report.italy_fallback = {
          applied: isIos && isItLocale && wrongCurrency,
          ios: isIos,
          italian_locale: isItLocale,
          sdk_currency: firstCurr || null,
          reason: isIos && isItLocale && wrongCurrency
            ? `StoreKit SDK sta restituendo ${firstCurr || 'nulla'}, ma il checkout Apple mostra EUR. UI corretta con fallback Italia (4,99 € / 44,99 €).`
            : null,
        };
      }
    } catch (e) {
      report.fetch_error = e?.message || String(e);
      setError(report.fetch_error);
    }
    setResult(report);
    setRunning(false);
  };

  const handleResetSession = async () => {
    setResetting(true);
    setResetLog(null);
    try {
      const log = await rcResetSession();
      setResetLog(log);
      // Immediately re-run the diagnostic after the reset, so the user
      // can see whether the storefront changed.
      await runDiagnostic();
    } catch (e) {
      setResetLog([{ step: 'rcResetSession', ok: false, error: e?.message || String(e) }]);
    }
    setResetting(false);
  };

  const handleCopy = async () => {
    if (!result) return;
    const dump = safeJson(result);
    try {
      await navigator.clipboard.writeText(dump);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {
      // Fallback for older WebViews — synthesize a textarea & execCommand.
      const ta = document.createElement('textarea');
      ta.value = dump; ta.style.position = 'fixed'; ta.style.opacity = '0';
      document.body.appendChild(ta); ta.select();
      try { document.execCommand('copy'); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch {}
      document.body.removeChild(ta);
    }
  };

  const detectedCurrency =
    result?.storekit_products?.[0]?.currencyCode
    || result?.packages_extracted?.[0]?.product_currencyCode;

  return (
    <section
      className={`rounded-2xl p-4 ${
        isDark ? 'bg-white/5 border border-white/10' : 'bg-gray-50 border border-gray-200'
      }`}
      data-testid="rc-price-diagnostic"
    >
      <div className="flex items-center gap-2 mb-3">
        <h3 className="font-semibold text-sm flex-1">
          Prezzo RevenueCat / App Store
        </h3>
      </div>

      <p className={`text-xs mb-3 ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
        Interroga RevenueCat per ottenere il prezzo dell'abbonamento sul tuo
        device. Confronta la <strong>valuta restituita</strong> con quella
        attesa per ciascun paese. Se la valuta non corrisponde al tuo storefront
        Apple ID, c'è un problema di sandbox tester o di configurazione prezzi
        in App Store Connect.
      </p>

      <button
        onClick={runDiagnostic}
        disabled={running || resetting}
        data-testid="rc-diagnostic-run-btn"
        className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-medium text-sm mb-2 ${
          running || resetting
            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
            : 'bg-[#E8334A] hover:bg-[#d12d40] text-white active:scale-95'
        } transition-all`}
      >
        {running ? <RefreshCw className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
        {running ? 'Esecuzione…' : 'Esegui diagnostica RC'}
      </button>

      <button
        onClick={handleResetSession}
        disabled={running || resetting}
        data-testid="rc-diagnostic-reset-btn"
        className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-xl font-medium text-xs mb-3 ${
          running || resetting
            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
            : isDark
              ? 'bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 border border-amber-500/40'
              : 'bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200'
        } transition-all`}
      >
        {resetting ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
        {resetting ? 'Reset in corso…' : 'Reset RevenueCat session (logOut + invalidate cache + sync)'}
      </button>

      {resetLog && (
        <div className={`rounded-xl p-3 mb-3 text-xs space-y-1 ${isDark ? 'bg-white/5' : 'bg-white border border-gray-200'}`}>
          <div className={`font-semibold mb-1 ${isDark ? 'text-white/80' : 'text-gray-700'}`}>Reset log:</div>
          {resetLog.map((entry, i) => (
            <div key={i} className="flex items-start gap-2 font-mono">
              <span className={entry.ok ? 'text-green-500' : 'text-red-500'}>
                {entry.ok ? '✓' : '✗'}
              </span>
              <span className={`flex-1 ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                {entry.step}
                {entry.error && <span className="text-red-500 ml-2">{entry.error}</span>}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Target countries reference table — always visible */}
      <div className={`rounded-xl mb-3 overflow-hidden ${isDark ? 'bg-white/5' : 'bg-white border border-gray-200'}`}>
        <div className={`px-3 py-2 text-xs font-semibold ${isDark ? 'text-white/80 bg-white/5' : 'text-gray-700 bg-gray-100'}`}>
          Paesi target (valuta attesa)
        </div>
        {TARGET_COUNTRIES.map((c) => {
          const matches = detectedCurrency && c.expected_currency === detectedCurrency;
          return (
            <div
              key={c.code}
              className={`flex items-center px-3 py-1.5 text-xs ${
                isDark ? 'border-t border-white/5' : 'border-t border-gray-100'
              } ${matches ? 'bg-green-500/10' : ''}`}
            >
              <span className="text-base mr-2">{c.flag}</span>
              <span className="flex-1">{c.name}</span>
              <span className={`font-mono ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
                {c.expected_currency}
              </span>
              {matches && <Check className="w-3.5 h-3.5 text-green-500 ml-2" />}
            </div>
          );
        })}
      </div>

      {/* Result block */}
      {result && (
        <div className="space-y-2 mb-3">
          {result.italy_fallback?.applied && (
            <div
              className="flex items-start gap-2 p-3 rounded-xl bg-amber-500/10 border border-amber-500/40 text-xs"
              data-testid="rc-italy-fallback-warning"
            >
              <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
              <div className={`flex-1 ${isDark ? 'text-white/90' : 'text-gray-800'}`}>
                <strong className="text-amber-500 block mb-1">Fallback Italia attivo</strong>
                {result.italy_fallback.reason}
              </div>
            </div>
          )}

          {error && (
            <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-500/10 border border-amber-500/30 text-xs">
              <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <strong className="text-amber-500">Errore:</strong>{' '}
                <span className={isDark ? 'text-white/80' : 'text-gray-700'}>{error}</span>
              </div>
            </div>
          )}

          <div className={`rounded-xl p-3 text-xs space-y-1 ${isDark ? 'bg-white/5' : 'bg-white border border-gray-200'}`}>
            <div className="grid grid-cols-[1fr_2fr] gap-2">
              <span className={isDark ? 'text-white/50' : 'text-gray-500'}>Storefront rilevato</span>
              <span className={`font-mono break-all ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {result.detected_storefront || '—'}
              </span>
              <span className={isDark ? 'text-white/50' : 'text-gray-500'}>Currency code</span>
              <span className={`font-mono ${detectedCurrency === 'EUR' ? 'text-green-500' : 'text-amber-500'} font-bold`}>
                {detectedCurrency || '—'}
              </span>
              <span className={isDark ? 'text-white/50' : 'text-gray-500'}>appUserID</span>
              <span className={`font-mono break-all text-[10px] ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                {result.appUserID || '—'}
              </span>
              <span className={isDark ? 'text-white/50' : 'text-gray-500'}>originalAppUserId</span>
              <span className={`font-mono break-all text-[10px] ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                {result.customerInfo?.originalAppUserId || '—'}
              </span>
              <span className={isDark ? 'text-white/50' : 'text-gray-500'}>currentOffering</span>
              <span className={`font-mono ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                {result.currentOfferingIdentifier || '—'}
              </span>
              <span className={isDark ? 'text-white/50' : 'text-gray-500'}>Entitlements attivi</span>
              <span className={`font-mono ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                {Object.keys(result.customerInfo?.entitlements?.active || {}).join(', ') || '(nessuno)'}
              </span>
              <span className={isDark ? 'text-white/50' : 'text-gray-500'}>Lingua device</span>
              <span className={`font-mono ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                {result.app_language}
              </span>
              <span className={isDark ? 'text-white/50' : 'text-gray-500'}>Plugin RC disponibile</span>
              <span className={`font-mono ${result.isPluginAvailable_Purchases ? 'text-green-500' : 'text-red-500'}`}>
                {String(result.isPluginAvailable_Purchases)}
              </span>
              <span className={isDark ? 'text-white/50' : 'text-gray-500'}>Packages trovati</span>
              <span className={`font-mono ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                {result.packages_extracted?.length ?? 0}
              </span>
            </div>
          </div>

          {result.packages_extracted?.map((pkg, idx) => (
            <div
              key={idx}
              className={`rounded-xl p-3 text-xs space-y-1 ${isDark ? 'bg-white/5' : 'bg-white border border-gray-200'}`}
            >
              <div className={`font-semibold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {pkg.packageType} · {pkg.identifier}
              </div>
              <div className="grid grid-cols-[1fr_2fr] gap-2">
                <span className={isDark ? 'text-white/50' : 'text-gray-500'}>price</span>
                <span className={`font-mono ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {pkg.product_price ?? '—'}
                </span>
                <span className={isDark ? 'text-white/50' : 'text-gray-500'}>priceString</span>
                <span className={`font-mono break-all ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {pkg.product_priceString ?? '—'}
                </span>
                <span className={isDark ? 'text-white/50' : 'text-gray-500'}>currencyCode</span>
                <span className={`font-mono ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {pkg.product_currencyCode ?? '—'}
                </span>
                <span className={isDark ? 'text-white/50' : 'text-gray-500'}>subscriptionPeriod</span>
                <span className={`font-mono ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {pkg.product_subscriptionPeriod ?? '—'}
                </span>
                <span className={isDark ? 'text-white/50' : 'text-gray-500'}>product id</span>
                <span className={`font-mono break-all text-[10px] ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                  {pkg.product_identifier ?? '—'}
                </span>
              </div>
            </div>
          ))}

          {/* Side-by-side: StoreKit (getProducts) vs Offering (getOfferings) */}
          {result.price_comparison?.length > 0 && (
            <div
              className={`rounded-xl p-3 text-xs ${
                isDark ? 'bg-white/5' : 'bg-white border border-gray-200'
              }`}
              data-testid="rc-diagnostic-comparison"
            >
              <div className={`font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Confronto StoreKit ↔ RevenueCat Offering
              </div>
              {result.price_comparison.map((row, i) => {
                const psMatch = row.priceString_match;
                const ccMatch = row.currency_match;
                return (
                  <div
                    key={i}
                    className={`mb-2 pb-2 ${i < result.price_comparison.length - 1 ? (isDark ? 'border-b border-white/10' : 'border-b border-gray-200') : ''}`}
                    data-testid={`rc-comparison-row-${row.product_id}`}
                  >
                    <div className={`font-mono text-[11px] mb-1 ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                      {row.product_id}
                    </div>
                    <div className="grid grid-cols-[1fr_1fr_1fr] gap-1 text-[10px]">
                      <span className={isDark ? 'text-white/50' : 'text-gray-500'}>campo</span>
                      <span className={isDark ? 'text-white/50' : 'text-gray-500'}>StoreKit</span>
                      <span className={isDark ? 'text-white/50' : 'text-gray-500'}>Offering</span>

                      <span className={isDark ? 'text-white/60' : 'text-gray-600'}>priceString</span>
                      <span className={`font-mono break-all ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {row.storekit_priceString ?? '—'}
                      </span>
                      <span className={`font-mono break-all ${psMatch === false ? 'text-red-500 font-bold' : (isDark ? 'text-white' : 'text-gray-900')}`}>
                        {row.offering_priceString ?? '—'}
                      </span>

                      <span className={isDark ? 'text-white/60' : 'text-gray-600'}>currency</span>
                      <span className={`font-mono ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {row.storekit_currency ?? '—'}
                      </span>
                      <span className={`font-mono ${ccMatch === false ? 'text-red-500 font-bold' : (isDark ? 'text-white' : 'text-gray-900')}`}>
                        {row.offering_currency ?? '—'}
                      </span>
                    </div>
                    <div className="mt-1 flex items-center gap-2 text-[10px]">
                      {psMatch === false && (
                        <span className="text-red-500 font-bold">⚠ priceString diverso</span>
                      )}
                      {ccMatch === false && (
                        <span className="text-red-500 font-bold">⚠ currency diversa</span>
                      )}
                      {psMatch === true && ccMatch === true && (
                        <span className="text-green-500 font-bold">✓ Coerente</span>
                      )}
                      {psMatch === null && ccMatch === null && (
                        <span className={isDark ? 'text-white/40' : 'text-gray-400'}>
                          dati mancanti — impossibile confrontare
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
              <div className={`text-[10px] mt-1 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                Nota: l&apos;app mostra il valore di <strong>StoreKit</strong>, che è esattamente
                quello che vedi nel popup di acquisto Apple. Se StoreKit e Offering
                divergono, la causa è la storefront di fallback del dashboard
                RevenueCat per utenti anonimi.
              </div>
            </div>
          )}
        </div>
      )}

      {result && (
        <button
          onClick={handleCopy}
          data-testid="rc-diagnostic-copy-btn"
          className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-xl font-medium text-sm transition-all ${
            copied
              ? 'bg-green-500 text-white'
              : isDark
                ? 'bg-white/10 text-white hover:bg-white/20'
                : 'bg-gray-200 text-gray-900 hover:bg-gray-300'
          }`}
        >
          {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          {copied ? 'Copiato!' : 'Copia log diagnostica RC'}
        </button>
      )}
    </section>
  );
}
